import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, MagnifyingGlass, UserMinus, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtTimestamp, useSortableTable, SortHeader} from './_lib';

export default function SuppressionList() {
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [q, setQ] = useState('');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/suppression?per_page=200&q=${encodeURIComponent(q)}`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [q]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);

	// Hooks must run on every render — keep them above the early return.
	const items = (data && data.items) || [];
	const bySource = (data && data.by_source) || {};
	const sort = useSortableTable(items, 'unsubscribed_at', 'desc');

	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-5xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="mb-8">
					<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
						<UserMinus size={28} weight="duotone" color="#ef4444"/> Suppression list
					</h1>
					<p className="text-sm text-slate-500 mt-1">Globally unsubscribed addresses — bounces, complaints, list-unsubscribe clicks. Auto-skipped on every send.</p>
				</div>

				<div className="flex flex-wrap gap-2 mb-6 text-xs">
					<span className="rounded-full bg-rose-50 border border-rose-200 px-3 py-1 text-rose-800">bounce: <strong>{bySource.bounce || 0}</strong></span>
					<span className="rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-amber-800">complaint: <strong>{bySource.complaint || 0}</strong></span>
					<span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">list-unsub: <strong>{bySource.list_unsubscribe || 0}</strong></span>
					<span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">manual: <strong>{bySource.manual || 0}</strong></span>
				</div>

				<div className="relative mb-4">
					<MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
					<input value={q} onChange={e=>setQ(e.target.value)} placeholder="search email or domain…" className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-300"/>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && (
					<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
						<table className="w-full text-sm">
							<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
								<tr>
									<SortHeader {...sort} column="email" className="text-left px-5 py-3">Email</SortHeader>
									<SortHeader {...sort} column="source" className="text-left px-3 py-3">Source</SortHeader>
									<SortHeader {...sort} column="unsubscribed_at" numeric className="text-left px-3 py-3">When</SortHeader>
								</tr>
							</thead>
							<tbody>
								{sort.rows.length === 0 ? (
									<tr><td colSpan={3} className="px-5 py-12 text-center text-sm text-slate-500">No suppression entries match.</td></tr>
								) : sort.rows.map((r,i) => (
									<tr key={`${r.email}-${i}`} className="border-t border-slate-100">
										<td className="px-5 py-2 text-slate-900">{r.email}</td>
										<td className="px-3 py-2 text-slate-700 text-xs">{r.source || '—'}</td>
										<td className="px-3 py-2 text-slate-500 text-xs">{fmtTimestamp(r.unsubscribed_at)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
